import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  CircularProgress,
} from '@gapartners/ui';
import type { SimpleContact } from '@/types';
import { Phone as PhoneIcon, Chat as ChatIcon } from '@mui/icons-material';
import type { RootState } from '@/store';

const formatTimer = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

interface IncomingCallModalProps {
  contact: SimpleContact | null;
  /** When true, shows "Incoming chat" and chat-specific copy. */
  isChat?: boolean;
  /** When true, this is a queue callback. Voice only. */
  isCallback?: boolean;
  /** When true, the callback was accepted — show timer instead of Accept/Decline. */
  isCallbackAccepted?: boolean;
  onAccept: () => void;
  onDecline: () => void;
  /** Cancels an outbound call while it is still dialing (not yet connected). */
  onCancel?: () => void;
}

export function IncomingCallModal({
  contact,
  isChat = false,
  isCallback = false,
  isCallbackAccepted = false,
  onAccept,
  onDecline,
  onCancel,
}: IncomingCallModalProps) {
  const { t } = useTranslation();
  const contactInformation = useSelector(
    (state: RootState) => state.call.contactInformation
  );

  const [callbackElapsed, setCallbackElapsed] = useState(0);

  useEffect(() => {
    if (!contact || !isCallback || !isCallbackAccepted) return;
    const startedAt = Date.now();
    const interval = setInterval(() => {
      setCallbackElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [contact, isCallback, isCallbackAccepted]);

  if (!contact) {
    return null;
  }
  // Single guarded read of all contact fields used during render — the
  // contact object may already be destroyed remotely (StateError).
  let isInbound = true;
  let contactId = '-';
  let queueName: string | null = null;
  try {
    isInbound = contact.isInbound();
    contactId = contact.getContactId();
    queueName = contact.getQueue()?.name ?? null;
  } catch {
    // Contact may already be destroyed remotely (StateError) — fall back to
    // safe defaults instead of crashing the render.
  }
  const titleKey = isCallback
    ? 'components.amazonConnect.callback'
    : isChat
      ? isInbound
        ? 'components.amazonConnect.incomingChat'
        : 'components.amazonConnect.outboundChat'
      : isInbound
        ? 'components.amazonConnect.incomingCall'
        : 'components.amazonConnect.outboundCall';
  const subtitleKey = isCallback
    ? 'components.amazonConnect.callbackSubtitle'
    : isChat
      ? isInbound
        ? 'components.amazonConnect.inboundChat'
        : 'components.amazonConnect.outboundChat'
      : isInbound
        ? 'components.amazonConnect.inboundCall'
        : 'components.amazonConnect.outboundCall';

  return (
    <Dialog open={Boolean(contact)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ textAlign: 'center' }} component="div">
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              bgcolor: isChat ? '#dcfce7' : '#dbeafe', // green-100 for chat, blue-100 for call
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isChat ? 'success.main' : 'primary.main',
            }}
          >
            {isChat ? (
              <ChatIcon sx={{ fontSize: 32 }} />
            ) : (
              <PhoneIcon sx={{ fontSize: 32 }} />
            )}
          </Box>
          {isChat && (
            <Box
              component="span"
              sx={{
                px: 1.25,
                py: 0.35,
                borderRadius: 1,
                fontSize: '0.7rem',
                fontWeight: 600,
                bgcolor: 'success.light',
                color: 'success.dark',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {t('components.amazonConnect.chatLabel', 'Chat')}
            </Box>
          )}
          {isCallback && (
            <Box
              component="span"
              sx={{
                px: 1.25,
                py: 0.35,
                borderRadius: 1,
                fontSize: '0.7rem',
                fontWeight: 600,
                bgcolor: '#fef3c7',
                color: '#92400e',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {t('components.amazonConnect.callbackLabel', 'Callback')}
            </Box>
          )}
        </Box>
        <Typography
          variant="h6"
          fontWeight={700}
          component="span"
          sx={{ display: 'block', textAlign: 'center' }}
        >
          {t(titleKey)}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t(subtitleKey)}
          </Typography>
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace' }}
          >
            {t('components.amazonConnect.contactId')}: {contactId}
          </Typography>

          {queueName && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <Box component="span" fontWeight={500}>
                  {t('components.amazonConnect.queue')}:
                </Box>{' '}
                {queueName}
              </Typography>
            </Box>
          )}
          {contactInformation?.authenticated !== undefined && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <Box component="span" fontWeight={500}>
                  {t('components.amazonConnect.authentication.label')}:
                </Box>{' '}
                {contactInformation.authenticated
                  ? t('components.amazonConnect.authentication.authenticated')
                  : t(
                      'components.amazonConnect.authentication.notAuthenticated'
                    )}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
        {isCallback && isCallbackAccepted ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              py: 1,
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              {t(
                'components.amazonConnect.dialingCustomer',
                'Dialing customer…'
              )}
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
            >
              {formatTimer(callbackElapsed)}
            </Typography>
          </Box>
        ) : isInbound ? (
          <>
            <Button variant="contained" color="primary" onClick={onAccept}>
              {t('components.amazonConnect.accept')}
            </Button>
            <Button variant="outlined" color="inherit" onClick={onDecline}>
              {t('components.amazonConnect.decline')}
            </Button>
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              py: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                {t('components.contactCenter.connecting', 'Connecting…')}
              </Typography>
            </Box>
            {onCancel && (
              <Button variant="outlined" color="inherit" onClick={onCancel}>
                {t('common.cancel', 'Cancel')}
              </Button>
            )}
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
}
